import React from 'react';

const scoreColor = (n) => {
  if (n >= 65) return '#059669';
  if (n >= 45) return '#D97706';
  return '#DC2626';
};

const matchLabel = (n) => {
  if (n >= 75) return 'Strong Match';
  if (n >= 55) return 'Moderate Match';
  return 'Low Match';
};

function splitSkills(str) {
  if (!str || str === 'Not provided' || str === 'Not available' || str === 'Not specified') return [];
  if (Array.isArray(str)) return str.map(s => String(s).trim()).filter(Boolean);
  return String(str).split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
}

/** Normalize tech lists inside a single project line, e.g. Tech: ['Python', 'Selenium'] */
function normalizeProjectEntry(entry) {
  return String(entry)
    .replace(/Tech:\s*\[([^\]]*)\]/gi, (_, inner) => {
      const techs = inner
        .split(/,\s*/)
        .map((t) => t.replace(/^['"]+|['"]+$/g, '').trim())
        .filter(Boolean);
      return `Tech: ${techs.length ? techs.join(', ') : 'Not specified'}`;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Format relevant projects for display. Unlike splitSkills(), does not split on commas
 * inside project text (e.g. Python tech arrays). Multiple projects are joined with " | ".
 */
function formatRelevantProjects(raw) {
  if (!raw || raw === 'Not provided' || raw === 'Not available' || raw === 'Not specified') {
    return '';
  }

  let items = [];

  if (Array.isArray(raw)) {
    items = raw.map((s) => String(s).trim()).filter(Boolean);
  } else {
    const str = String(raw).trim();
    if (str.startsWith('[')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) {
          items = parsed.map((p) => String(p).trim()).filter(Boolean);
        }
      } catch (e) {
        // not JSON — fall through
      }
    }
    if (items.length === 0) {
      if (/\s*\|\s*/.test(str)) {
        items = str.split(/\s*\|\s*/).map((s) => s.trim()).filter(Boolean);
      } else {
        items = [str];
      }
    }
  }

  return items.map(normalizeProjectEntry).filter(Boolean).join(' | ');
}

function hasData(v) {
  return v && v !== 'Not provided' && v !== 'Not available' && v !== 'Not specified';
}

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function BannerArc({ pct }) {
  const cx = 44, cy = 48, R = 36;
  const angle = Math.PI - (pct / 100) * Math.PI;
  const endX = (cx + R * Math.cos(angle)).toFixed(1);
  const endY = (cy - R * Math.sin(angle)).toFixed(1);
  const large = 0;
  const fillPath = pct > 0
    ? `M${cx - R},${cy} A${R},${R} 0 ${large},1 ${endX},${endY}`
    : '';
  return (
    <svg width="88" height="52" viewBox="0 0 88 52">
      <path d={`M${cx - R},${cy} A${R},${R} 0 0,1 ${cx + R},${cy}`}
        fill="none" stroke="rgba(255,255,255,.2)" strokeWidth="7" strokeLinecap="round" />
      {pct > 0 && (
        <path d={fillPath} fill="none" stroke="rgba(255,255,255,.85)"
          strokeWidth="7" strokeLinecap="round" />
      )}
      <text x={cx} y={cy - 12} textAnchor="middle" fontSize="13" fontWeight="700"
        fill="white" fontFamily="monospace">{pct}%</text>
    </svg>
  );
}

function ScoreRing({ pct, color }) {
  const r = 21;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - pct / 100);
  return (
    <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r={r} fill="none" stroke="#E5E7EB" strokeWidth="5" />
        <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={C} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 26 26)"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color }}>
        {pct}%
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: '#6B7280', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
      {children}
      <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
    </div>
  );
}

const CandidateReportModal = ({
  candidate,
  skillWeightages = [],
  rankingDate,
  totalCandidates,
  onClose,
  onWhatsApp,
  onShortlist,
}) => {
  if (!candidate) return null;

  const name = candidate.name || candidate.candidateName || 'Candidate';
  const score = Math.round(Number(candidate.total_score ?? candidate.totalScore ?? 0));
  const rank = candidate.rank ?? '—';
  const role = candidate.current_position || candidate.currentPosition || '';
  const email = candidate.email || '';
  const mobile = candidate.mobile || candidate.phone || '';
  const experience = candidate.experience;
  const company = candidate.company || '';

  const sc = scoreColor(score);
  const ml = matchLabel(score);

  const verdictTitle = score >= 75
    ? 'Strong Match — well aligned with the role'
    : score >= 55
      ? 'Moderate Match — partially qualified, some gaps'
      : 'Low Match — significant qualification gaps';

  const ps = candidate.parameter_scores || candidate.parameterScores;

  const scoreBreakdown = skillWeightages.length > 0
    ? skillWeightages.map(sk => {
        let paramScore = 0;
        if (ps && typeof ps === 'object') {
          const key = Object.keys(ps).find(k => k === sk.skillName)
            || Object.keys(ps).find(k => k.toLowerCase() === sk.skillName.toLowerCase());
          if (key != null) paramScore = Math.round(Number(ps[key]));
        }
        return { name: sk.skillName, weight: sk.weightage, score: paramScore };
      })
    : ps
      ? Object.entries(ps).map(([n, v]) => ({ name: n, weight: null, score: Math.round(Number(v)) }))
      : [];

  const matchingSkills = splitSkills(candidate.matching_skills || candidate.matchingSkills);
  const missingSkills = splitSkills(candidate.missing_skills || candidate.missingSkills);

  const strengths = matchingSkills.slice(0, 5);
  const gaps = missingSkills.slice(0, 5);

  const education = candidate.education;
  const relevantProjects = candidate.relevant_projects || candidate.relevantProjects;
  const matchSummary = candidate.match_summary || candidate.matchSummary;

  const verdictIconBg = score >= 75 ? '#ECFDF5' : score >= 55 ? '#FFFBEB' : '#FEF2F2';
  const verdictIconColor = sc;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1200 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#FFFFFF', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,.14)', animation: 'crm-slide-up .28s cubic-bezier(.34,1.4,.64,1)' }}
      >
        <style>{`@keyframes crm-slide-up{from{opacity:0;transform:translateY(20px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>

        {/* ── BANNER ── */}
        <div style={{ background: 'linear-gradient(135deg,#0041C2 0%,#1A5FD4 60%,#2E6FD4 100%)', padding: '22px 28px 18px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            {/* Left: avatar + info */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {(name[0] || '?').toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,.65)', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 500, marginBottom: 2 }}>Candidate Report</div>
                <div style={{ fontSize: 19, fontWeight: 700, color: '#fff', letterSpacing: '-.01em', marginBottom: 2 }}>{name}</div>
                {hasData(role) && <div style={{ fontSize: 13, color: 'rgba(255,255,255,.75)', marginBottom: 7 }}>{role}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, fontFamily: 'monospace' }}>
                    Rank #{rank}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color: '#fff', border: '1px solid rgba(255,255,255,.3)', background: 'rgba(255,255,255,.15)' }}>
                    {ml}
                  </span>
                </div>
              </div>
            </div>
            {/* Right: close button stacked above arc */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
              <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,.15)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, lineHeight: 1 }}>×</button>
              <BannerArc pct={score} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {email && email !== 'N/A' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 7, padding: '5px 10px', fontSize: 11, color: 'rgba(255,255,255,.9)', fontWeight: 500 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                {email}
              </div>
            )}
            {mobile && mobile !== 'Not provided' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 7, padding: '5px 10px', fontSize: 11, color: 'rgba(255,255,255,.9)', fontWeight: 500 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                {mobile}
              </div>
            )}
            {hasData(experience) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 7, padding: '5px 10px', fontSize: 11, color: 'rgba(255,255,255,.9)', fontWeight: 500 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>
                {experience} yrs{hasData(company) ? ` · ${company}` : ''}
              </div>
            )}
          </div>
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>

          {/* VERDICT BAR */}
          <div style={{ background: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: verdictIconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 17, fontWeight: 700, color: verdictIconColor }}>
              {score >= 75 ? '✓' : score >= 55 ? '⚡' : '!'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B3E', marginBottom: 2 }}>{verdictTitle}</div>
              {(matchingSkills.length > 0 || missingSkills.length > 0) && (
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {matchingSkills.length > 0 && `${matchingSkills.length} matching skill${matchingSkills.length > 1 ? 's' : ''}`}
                  {matchingSkills.length > 0 && missingSkills.length > 0 && ' · '}
                  {missingSkills.length > 0 && `${missingSkills.length} gap${missingSkills.length > 1 ? 's' : ''} identified`}
                </div>
              )}
            </div>
            <ScoreRing pct={score} color={sc} />
          </div>

          {/* STRENGTHS / GAPS */}
          {(strengths.length > 0 || gaps.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: strengths.length > 0 && gaps.length > 0 ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 18 }}>
              {strengths.length > 0 && (
                <div style={{ background: '#ECFDF5', border: '1px solid rgba(5,150,105,.18)', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#059669', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Strengths
                  </div>
                  {strengths.map((s, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#374151', padding: '3px 0', borderBottom: i < strengths.length - 1 ? '1px solid rgba(0,0,0,.06)' : 'none', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.5 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', flexShrink: 0, marginTop: 5 }} />
                      {s}
                    </div>
                  ))}
                </div>
              )}
              {gaps.length > 0 && (
                <div style={{ background: '#FEF2F2', border: '1px solid rgba(220,38,38,.15)', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#DC2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    Gaps
                  </div>
                  {gaps.map((g, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#374151', padding: '3px 0', borderBottom: i < gaps.length - 1 ? '1px solid rgba(0,0,0,.06)' : 'none', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.5 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#DC2626', flexShrink: 0, marginTop: 5 }} />
                      {g}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SCORE BREAKDOWN */}
          {scoreBreakdown.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionLabel>AI Score Breakdown</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {scoreBreakdown.map((sk, i) => (
                  <div key={i} style={{ background: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 10, padding: '11px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{sk.name}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(sk.score), fontFamily: 'monospace' }}>{sk.score}%</span>
                    </div>
                    {sk.weight != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                        <span style={{ fontSize: 9, color: '#6B7280' }}>Weight:</span>
                        <span style={{ fontSize: 9, fontWeight: 600, color: '#374151' }}>{sk.weight}%</span>
                      </div>
                    )}
                    <div style={{ height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: scoreColor(sk.score), width: `${sk.score}%`, transition: 'width .7s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MATCHING SKILLS */}
          {matchingSkills.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <SectionLabel>Matching Skills</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {matchingSkills.map((s, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 6, background: '#ECFDF5', color: '#059669', border: '1px solid rgba(5,150,105,.2)' }}>✓ {s}</span>
                ))}
              </div>
            </div>
          )}

          {/* MISSING SKILLS */}
          {missingSkills.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <SectionLabel>Missing Skills</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {missingSkills.map((s, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 6, background: '#FEF2F2', color: '#DC2626', border: '1px solid rgba(220,38,38,.15)' }}>✗ {s}</span>
                ))}
              </div>
            </div>
          )}

          {/* EDUCATION + EXPERIENCE */}
          {(hasData(education) || hasData(experience)) && (
            <div style={{ display: 'grid', gridTemplateColumns: hasData(education) && hasData(experience) ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 18 }}>
              {hasData(education) && (
                <div>
                  <SectionLabel>Education</SectionLabel>
                  <div style={{ background: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: '#EEF3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0041C2" strokeWidth="1.8" strokeLinecap="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B3E', lineHeight: 1.45 }}>{education}</div>
                  </div>
                </div>
              )}
              {hasData(experience) && (
                <div>
                  <SectionLabel>Experience</SectionLabel>
                  <div style={{ background: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#0041C2', fontFamily: 'monospace', lineHeight: 1 }}>
                        {String(experience).replace(/[^\d.]/g, '') || experience}
                      </div>
                      <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 500 }}>Years</div>
                    </div>
                    {hasData(company) && (
                      <>
                        <div style={{ width: 1, height: 34, background: '#E5E7EB' }} />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B3E' }}>{company}</div>
                          {hasData(role) && <div style={{ fontSize: 11, color: '#6B7280' }}>{role}</div>}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* RELEVANT PROJECTS */}
          {hasData(relevantProjects) && (
            <div style={{ marginBottom: 18 }}>
              <SectionLabel>Relevant Projects</SectionLabel>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, background: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px' }}>
                {formatRelevantProjects(relevantProjects)}
              </div>
            </div>
          )}

          {/* AI MATCH SUMMARY */}
          {hasData(matchSummary) && (
            <div>
              <SectionLabel>AI Match Summary</SectionLabel>
              <div style={{ background: 'linear-gradient(135deg,#EEF3FF 0%,#F0F5FF 100%)', border: '1px solid rgba(0,65,194,.15)', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {matchSummary}
              </div>
            </div>
          )}

        </div>

        {/* ── FOOTER ── */}
        <div style={{ padding: '14px 28px', borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F7F8FA', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {totalCandidates ? `Rank #${rank} of ${totalCandidates}` : `Rank #${rank}`}
            {rankingDate ? ` · ${fmtDate(rankingDate)}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {onShortlist && (
              <button
                onClick={onShortlist}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: '1px solid #E5E7EB', color: '#374151', fontFamily: 'inherit' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></svg>
                Shortlist
              </button>
            )}
            {onWhatsApp && (
              <button
                onClick={onWhatsApp}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg,#059669,#10B981)', color: '#fff', border: 'none', fontFamily: 'inherit' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                Send WhatsApp
              </button>
            )}
            <button
              onClick={onClose}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: '#fff', border: 'none', fontFamily: 'inherit' }}
            >
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CandidateReportModal;
